// ─── Match State ────────────────────────────────────────────────────────────

export type MatchStatus =
  | 'scheduled'     // non ancora iniziata
  | 'warmup'        // riscaldamento
  | 'live'          // in corso
  | 'pause'         // pausa tra set
  | 'timeout_home'  // timeout squadra casa
  | 'timeout_away'  // timeout squadra ospite
  | 'finished'      // terminata
  | 'postponed'     // rinviata

export interface ScorebugThemeConfig {
  accent?: string
  accentStrong?: string
  serviceColor?: string
  mainBg?: string
  detailsBg?: string
  centerSlotBg?: string
  centerImageUrl?: string
  chipBg?: string
  logoBg?: string
  setsBg?: string
  borderColor?: string
  chipBorder?: string
  text?: string
  textSoft?: string
  textMuted?: string
  secondaryText?: string
}

export interface MatchState {
  matchId: string
  homeTeam: string
  awayTeam: string
  homeTeamLogo?: string
  awayTeamLogo?: string
  currentSet: number                              // 1-5
  setsWon: { home: number; away: number }
  currentScore: { home: number; away: number }
  setHistory: Array<{ home: number; away: number }>
  isTiebreak: boolean                             // 5° set: vince a 15 con +2
  isMatchFinished: boolean
  winner: 'home' | 'away' | null
  serviceTeam: 'home' | 'away' | null
  firstServer: 'home' | 'away' | null
  timeoutsUsed: { home: number; away: number }   // max 2 per set, reset a ogni set
  status: MatchStatus
  scheduledAt?: string                            // ISO datetime per partite programmate
  startedAt?: string
  streamUrl?: string
  visible: boolean                                // se false non appare nella pubblicazione
  position?: number                               // ordine di visualizzazione (opzionale)
  leagueShort?: string                            // acronimo campionato (es. "1DM") per partite extra
  operatorUid?: string                            // codice accesso operatore (link ?uid=...)
  gameId?: string                                  // UUID univoco per sessione di partita (generato al primo salvataggio / RESET_MATCH)
  fipavMatchNumber?: string                       // numero gara FIPAV (es. "5786") per auto-archivio
  scorebugTheme?: ScorebugThemeConfig             // override CSS del pannello ScorebugOverlay
  scorebugServiceTeam?: 'home' | 'away' | null    // override servizio solo per overlay regia
  scorebugCommentaryVisible?: boolean             // mostra/nasconde il pannello commentary con animazione regia
  commentaryAvailable?: boolean                   // false se l'AI commentary non è temporaneamente disponibile
  commentaryUnavailableReason?: string | null     // motivo sintetico per UI operatore
  operatorConnected?: boolean                     // true se un operatore ha aperto il link live con uid
  operatorLastSeenAt?: string
  operatorLastIp?: string | null
  operatorLastUserAgent?: string | null
}

export const defaultMatchState: MatchState = {
  matchId: '',
  homeTeam: 'Dinamo Molfetta',
  awayTeam: '',
  currentSet: 1,
  setsWon: { home: 0, away: 0 },
  currentScore: { home: 0, away: 0 },
  setHistory: [],
  isTiebreak: false,
  isMatchFinished: false,
  winner: null,
  serviceTeam: null,
  firstServer: null,
  timeoutsUsed: { home: 0, away: 0 },
  status: 'scheduled',
  visible: true,
  scorebugCommentaryVisible: true,
  commentaryAvailable: true,
  commentaryUnavailableReason: null,
  operatorConnected: false,
  operatorLastSeenAt: undefined,
  operatorLastIp: null,
  operatorLastUserAgent: null,
}

// ─── Match Events ────────────────────────────────────────────────────────────

export type EventType =
  | 'ace'
  | 'attack'
  | 'block'
  | 'defense'
  | 'opponent_error'
  | 'substitution'
  | 'yellow_card'
  | 'red_card'
  | 'expulsion'
  | 'disqualification'
  | 'timeout_home'
  | 'timeout_away'
  | 'set_start'
  | 'set_end'
  | 'match_end'
  | 'manual'

export interface MatchEvent {
  id: string
  matchId: string
  occurredAt: string          // ISO timestamp
  eventType: EventType
  description: string
  operatorNote?: string
  aiCommentary?: string
  isAI: boolean
  setNumber: number
  score?: { home: number; away: number }
  scoringTeam?: 'home' | 'away'
}

// ─── API Payloads ─────────────────────────────────────────────────────────────

export interface CommentaryRequest {
  matchId: string
  homeTeam: string
  awayTeam: string
  currentSet: number
  homeScore: number
  awayScore: number
  setsWon: { home: number; away: number }
  setHistory: Array<{ home: number; away: number }>
  eventType: EventType
  operatorNote?: string
  serviceTeam: 'home' | 'away' | null
  previousServiceTeam?: 'home' | 'away' | null
  eventTeam?: 'home' | 'away' | null   // squadra coinvolta dall'evento, anche se non assegna un punto
  scoringTeam?: 'home' | 'away' | null  // chi ha appena segnato il punto
  gender?: 'M' | 'F'           // genere della squadra (M = maschile, F = femminile)
  recentCommentary?: string[]              // ultimi commenti AI già pubblicati (più recente prima)
  semanticallySimilarCommentary?: string[] // commenti semanticamente simili (da Qdrant) da evitare
  recentActions?: string[]      // ultimi eventi in campo formattati (più recente prima)
  timingContext?: string        // contesto temporale: durata partita/set, serie, timeout
  lastPointElapsedMs?: number   // millisecondi trascorsi dall'ultimo punto registrato
}

export interface CommentaryResponse {
  commentary: string | null
  eventType: EventType
  matchId: string
}

// ─── WebSocket Messages ───────────────────────────────────────────────────────

export interface CommentaryPayload {
  commentary: string
}

export interface WsMessage {
  type: 'score_update' | 'event' | 'set_end' | 'match_end' | 'status_change' | 'stream_url' | 'commentary'
  matchId: string
  payload: Partial<MatchState> | MatchEvent | CommentaryPayload
  timestamp: string
  /** ID stabile (historyId o eventId) che collega score_update e commentary alla stessa riga */
  sourceId?: string
}

// ─── FIPAV / Microservice ─────────────────────────────────────────────────────

export type TeamId = 'prima_divisione_maschile' | 'prima_divisione_femminile' | 'serie_d_maschile'

export interface TeamConfig {
  id: TeamId
  label: string
  short: string
  gender: 'M' | 'F'
  category: string
  comitatoId: number
  cId: number
  color: string
}

export const TEAMS: Record<TeamId, TeamConfig> = {
  prima_divisione_maschile: {
    id: 'prima_divisione_maschile',
    label: 'Prima Divisione Maschile',
    short: '1DM',
    gender: 'M',
    category: 'Prima Divisione',
    comitatoId: 10,
    cId: 85999,
    color: '#56B7FF',
  },
  prima_divisione_femminile: {
    id: 'prima_divisione_femminile',
    label: 'Prima Divisione Femminile',
    short: '1DF',
    gender: 'F',
    category: 'Prima Divisione',
    comitatoId: 10,
    cId: 85979,
    color: '#FF5C9A',
  },
  serie_d_maschile: {
    id: 'serie_d_maschile',
    label: 'Serie D Maschile',
    short: 'SDM',
    gender: 'M',
    category: 'Serie D',
    comitatoId: 9,
    cId: 85296,
    color: '#59D87A',
  },
}
